import React, { useState } from 'react';
import { NextUI } from '@/components/NextUI';
import FormCard from '../../../../components/Layouts/Default/FormCard';
import Button from '@/components/NextUI/Button';
import { useAppDispatch } from '@/hooks/redux';
import { showToast } from '@/store/slices/toastActions';
import useAuth from '@/hooks/useAuth';
import { z } from 'zod';
import { changePassword, clearMessageAndError } from '@/store/slices/authSlice';
import { Spinner } from '@heroui/spinner';
import { useTranslations } from 'next-intl';

const ChangePassword = () => {
  const t = useTranslations('change_password');
  const dispatch = useAppDispatch();
  const { fbUser, loading } = useAuth();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const resetPasswordSchema = z
    .object({
      newPassword: z
        .string()
        .min(6, { message: t('password_length') })
        .regex(/[A-Z]/, { message: t('password_uppercase') })
        .regex(/[0-9]/, { message: t('password_number') })
        .max(100, { message: t('password_max') }),
      repeatPassword: z.string(),
    })
    .refine((data) => data.newPassword === data.repeatPassword, {
      message: t('password_mismatch'),
      path: ['confirmPassword'],
    });

  React.useEffect(() => {
    dispatch(clearMessageAndError());
  }, []);

  const handleChangePassword = async (payload: FormData) => {
    const formData = {
      currentPassword: payload.get('currentPassword') as string,
      newPassword: payload.get('newPassword') as string,
      repeatPassword: payload.get('repeatPassword') as string,
    };

    const validationResult = resetPasswordSchema.safeParse(formData);

    if (formData.newPassword !== formData.repeatPassword) {
      dispatch(
        showToast({
          text: t('passwords_not_match'),
          variant: 'danger',
        })
      );
      return;
    }

    if (!validationResult.success) {
      const formErrors: { [key: string]: string } = {};
      validationResult.error.errors.forEach((error) => {
        formErrors[error.path[0]] = error.message;
      });
      setErrors(formErrors);
      return;
    }

    try {
      const result = await dispatch(
        changePassword({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
          fbUser,
        })
      ).unwrap();

      dispatch(
        showToast({
          text: result.message,
          variant: 'info',
        })
      );
    } catch (err: any) {
      dispatch(
        showToast({
          text: err.message || t('update_error'),
          variant: 'danger',
        })
      );
    }
  };

  return (
    <NextUI.Modal
      className="max-w-[500px]"
      classNames={{ content: 'py-8 !px-4 md:!px-8' }}
      defaultOpen={true}
      name={'ChangePassword'}
    >
      <h2 className="h5 font-semibold text-start text-dark">{t('title')}</h2>

      <FormCard
        isAction={false}
        className="mt-8 "
        classNames={{
          actionButton: 'max-w-[160px]',
          actionWrapper: 'mt-8 pb-6 inline-flex flex-col items-end p-0',
          formBody: 'p-0',
        }}
        action={handleChangePassword}
      >
        <>
          {loading ? (
            <div className="flex items-center justify-center h-20">
              <Spinner />
            </div>
          ) : (
            <>
              <NextUI.Input
                type="password"
                autoComplete="current-password"
                formNoValidate
                variant="outline"
                radius="sm"
                placeholder={t('current_password_placeholder')}
                name="currentPassword"
              />

              <NextUI.Input
                type="password"
                autoComplete="new-password"
                formNoValidate
                variant="outline"
                radius="sm"
                placeholder={t('new_password_placeholder')}
                className="mt-5"
                name="newPassword"
                message={errors.newPassword}
                messageVariant={errors.newPassword ? 'danger' : 'default'}
              />

              <NextUI.Input
                type="password"
                autoComplete="new-password"
                formNoValidate
                variant="outline"
                radius="sm"
                placeholder={t('repeat_password_placeholder')}
                className="mt-5"
                name="repeatPassword"
                message={errors.repeatPassword}
                messageVariant={errors.repeatPassword ? 'danger' : 'default'}
              />

              <div className="flex justify-end gap-4 mt-8">
                <Button
                  variant="outline"
                  className="w-[160px]"
                  disabled={loading}
                  onClick={() => {
                    // dispatch(
                    //   showModal({
                    //     modalKey: '',
                    //   })
                    // );
                  }}
                >
                  {t('cancel_button')}
                </Button>

                <Button className="w-[160px]" type="submit" isLoading={loading}>
                  {loading ? '' : t('change_button')}
                </Button>
              </div>
            </>
          )}
        </>
      </FormCard>
    </NextUI.Modal>
  );
};

export default ChangePassword;
